// src/services/mongoValetierClients.js
// Service clients Valetier utilisant MongoDB via API backend

const API_URL = 'https://valetier.maisonceronne.com/api';

const baseHeaders = {
  "Content-Type": "application/json",
  "Cache-Control": "no-store, no-cache, must-revalidate",
  "x-database": "valetier"  // ← Base MongoDB pour Valetier
};

function getUserHeaders() {
  const orgId = 'org_valetier';
  const userEmail = localStorage.getItem('userEmail') || 'valetier@maisonceronne.com';
  
  return {
    "x-user-id": userEmail,
    "x-user-email": userEmail,
    "x-organization-id": orgId
  };
}

function getRequestHeaders() {
  return { ...baseHeaders, ...getUserHeaders() };
}

function okJson(res) {
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

/**
 * Fonction générique pour interroger MongoDB via l'API
 */
async function mongoFind(collectionName, filter = {}, options = {}) {
  const body = {
    filter: filter,
    skip: options.skip || 0,
    limit: options.limit || 1000,
    sort: options.sort || {}
  };

  const res = await fetch(`${API_URL}/${collectionName}/find`, {
    method: "POST",
    headers: getRequestHeaders(),
    body: JSON.stringify(body),
  }).then(okJson);

  return res?.documents || [];
}

/**
 * Insère un document dans MongoDB
 */
export async function mongoInsertOne(collectionName, document) {
  const body = { document };

  const res = await fetch(`${API_URL}/${collectionName}/insertOne`, {
    method: "POST",
    headers: getRequestHeaders(),
    body: JSON.stringify(body),
  }).then(okJson);

  return res;
}

/**
 * Met à jour un document dans MongoDB
 */
async function mongoUpdateOne(collectionName, filter, update) {
  const body = { 
    filter,
    update: { $set: update }
  };

  const res = await fetch(`${API_URL}/${collectionName}/updateOne`, {
    method: "POST",
    headers: getRequestHeaders(),
    body: JSON.stringify(body),
  }).then(okJson);

  return res;
}

/**
 * Supprime un document dans MongoDB
 */
async function mongoDeleteOne(collectionName, filter) {
  const body = { filter };

  const res = await fetch(`${API_URL}/${collectionName}/deleteOne`, {
    method: "POST",
    headers: getRequestHeaders(),
    body: JSON.stringify(body),
  }).then(okJson);

  return res;
}

// ============================================
// FONCTIONS MÉTIER POUR CLIENTS VALETIER
// ============================================

/**
 * Normalise une chaîne pour la comparaison
 */
export function normalizeString(str) {
  if (!str) return '';
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Normalise un numéro de téléphone (gère +33 → 06)
 */
function normalizePhone(phone) {
  if (!phone) return '';
  
  // Enlever espaces, tirets, points, parenthèses
  let cleaned = phone.replace(/[\s\-.()\u00A0]/g, '');
  
  // Convertir +33 → 0
  if (cleaned.startsWith('+33')) {
    cleaned = '0' + cleaned.substring(3);
  } else if (cleaned.startsWith('0033')) {
    cleaned = '0' + cleaned.substring(4);
  }
  
  // Tout en chiffres uniquement
  cleaned = cleaned.replace(/[^0-9]/g, '');
  
  return cleaned;
}

/**
 * Récupère le numéro de client maximum pour générer le suivant
 */
export async function getMaxClientNumber() {
  try {
    const clients = await mongoFind('clients', {}, { 
      sort: { clientNumber: -1 },
      limit: 1 
    });
    
    if (clients.length === 0) return 0;
    
    return parseInt(clients[0].clientNumber || '0');
  } catch (error) {
    console.error('Erreur récupération max clientNumber:', error);
    return 0;
  }
}

/**
 * Récupère le numéro de membre club maximum pour générer le suivant
 */
export async function getMaxClubMemberNumber() {
  try {
    const members = await mongoFind('clients', 
      { isClubMember: true },
      { 
        sort: { clubMemberNumber: -1 },
        limit: 1 
      }
    );
    
    if (members.length === 0) return 0;
    
    return parseInt(members[0].clubMemberNumber || '0');
  } catch (error) {
    console.error('Erreur récupération max clubMemberNumber:', error);
    return 0;
  }
}

/**
 * Cherche un client par email
 */
export async function findClientByEmail(email) {
  try {
    if (!email) return null;
    
    const normalizedEmail = normalizeString(email);
    
    const clients = await mongoFind('clients', {
      'searchKeys.normalizedEmail': normalizedEmail
    }, { limit: 1 });
    
    if (clients.length === 0) return null;
    
    // ✅ Préférer client.id (Firebase ID)
    return {
      ...clients[0],
      id: clients[0].id || clients[0]._id
    };
  } catch (error) {
    console.error('Erreur recherche client par email:', error);
    return null;
  }
}

/**
 * Cherche un client par téléphone
 * Compare les 9 derniers chiffres pour gérer tous les formats (+33, 0033, 33, 06)
 */
export async function findClientByPhone(phone) {
  try {
    if (!phone) return null;
    
    // Extraire les 9 derniers chiffres
    const last9Digits = phone.replace(/[^0-9]/g, '').slice(-9);
    
    if (last9Digits.length !== 9) return null;
    
    const clients = await mongoFind('clients', {
      'searchKeys.normalizedPhone': { $regex: last9Digits + '$' }
    }, { limit: 1 });
    
    if (clients.length === 0) return null;
    
    // ✅ Préférer client.id (Firebase ID)
    return {
      ...clients[0],
      id: clients[0].id || clients[0]._id
    };
  } catch (error) {
    console.error('Erreur recherche client par téléphone:', error);
    return null;
  }
}

/**
 * Cherche un client par nom ET prénom
 */
export async function findClientByName(firstName, name) {
  try {
    if (!firstName || !name) return null;
    
    const normalizedFirstName = normalizeString(firstName);
    const normalizedName = normalizeString(name);
    
    const clients = await mongoFind('clients', {
      'searchKeys.normalizedFirstName': normalizedFirstName,
      'searchKeys.normalizedName': normalizedName
    }, { limit: 1 });
    
    if (clients.length === 0) return null;
    
    // ✅ Préférer client.id (Firebase ID)
    return {
      ...clients[0],
      id: clients[0].id || clients[0]._id
    };
  } catch (error) {
    console.error('Erreur recherche client par nom:', error);
    return null;
  }
}

// ============================================
// 🔍 FONCTIONS DE RECHERCHE DANS SIRVOY ARCHIVES
// ============================================

/**
 * Fonction pour interroger la base Sirvoy (MongoDB)
 */
async function mongoFindSirvoy(collectionName, filter = {}, options = {}) {
  const body = {
    filter: filter,
    skip: options.skip || 0,
    limit: options.limit || 1000,
    sort: options.sort || {}
  };

  const sirvoyHeaders = {
    "Content-Type": "application/json",
    "Cache-Control": "no-store, no-cache, must-revalidate",
    "x-database": "sirvoy_historique"
  };

  const userHeaders = getUserHeaders();

  const res = await fetch(`${API_URL}/${collectionName}/find`, {
    method: "POST",
    headers: { ...sirvoyHeaders, ...userHeaders },
    body: JSON.stringify(body),
  }).then(okJson);

  return res?.documents || [];
}

/**
 * Cherche un client Sirvoy par email
 */
async function findSirvoyClientByEmail(email) {
  try {
    if (!email) return null;
    
    const normalizedEmail = normalizeString(email);
    
    const clients = await mongoFindSirvoy('clients', {
      'searchKeys.normalizedEmail': normalizedEmail
    }, { limit: 1 });
    
    if (clients.length === 0) return null;
    
    return {
      ...clients[0],
      name: clients[0].lastName || clients[0].name,
      id: clients[0]._id,
      isSirvoyArchive: true,
      source: 'sirvoy'
    };
  } catch (error) {
    console.error('Erreur recherche client Sirvoy par email:', error);
    return null;
  }
}

/**
 * Cherche un client Sirvoy par téléphone
 */
/**
 * Cherche un client Sirvoy par téléphone
 * Compare les 9 derniers chiffres pour gérer tous les formats (+33, 0033, 33, 06)
 */
async function findSirvoyClientByPhone(phone) {
  try {
    if (!phone) return null;
    
    // Extraire les 9 derniers chiffres
    const last9Digits = phone.replace(/[^0-9]/g, '').slice(-9);
    
    if (last9Digits.length !== 9) return null;
    
    const clients = await mongoFindSirvoy('clients', {
      'searchKeys.normalizedPhone': { $regex: last9Digits + '$' }
    }, { limit: 1 });
    
    if (clients.length === 0) return null;
    
    return {
      ...clients[0],
      name: clients[0].lastName || clients[0].name,
      id: clients[0]._id,
      isSirvoyArchive: true,
      source: 'sirvoy'
    };
  } catch (error) {
    console.error('Erreur recherche client Sirvoy par téléphone:', error);
    return null;
  }
}

/**
 * Cherche un client Sirvoy par nom ET prénom
 */
async function findSirvoyClientByName(firstName, name) {
  try {
    if (!firstName || !name) return null;
    
    const normalizedFirstName = normalizeString(firstName);
    const normalizedName = normalizeString(name);
    
    const clients = await mongoFindSirvoy('clients', {
      'searchKeys.normalizedFirstName': normalizedFirstName,
      'searchKeys.normalizedName': normalizedName
    }, { limit: 1 });
    
    if (clients.length === 0) return null;
    
    return {
      ...clients[0],
      name: clients[0].lastName || clients[0].name,
      id: clients[0]._id,
      isSirvoyArchive: true,
      source: 'sirvoy'
    };
  } catch (error) {
    console.error('Erreur recherche client Sirvoy par nom:', error);
    return null;
  }
}

/**
 * Cherche un client existant selon les critères de matching
 * (Email > Téléphone > Nom+Prénom)
 * Cherche EN PARALLÈLE dans VALETIER + SIRVOY pour optimiser les performances
 */
export async function findExistingClient(clientData) {
  try {
    // ============================================
    // 1️⃣ RECHERCHE PAR EMAIL (priorité absolue)
    // ============================================
    if (clientData.email) {
      // Recherche parallèle Valetier + Sirvoy
      const [valetierClient, sirvoyClient] = await Promise.all([
        findClientByEmail(clientData.email),
        findSirvoyClientByEmail(clientData.email)
      ]);
      
      // Priorité : Valetier > Sirvoy
      if (valetierClient) {
        console.log('✅ Client VALETIER trouvé par email:', valetierClient.id);
        return valetierClient;
      }
      
      if (sirvoyClient) {
        console.log('📦 Client SIRVOY trouvé par email:', sirvoyClient.email);
        return sirvoyClient;
      }
    }
    
    // ============================================
    // 2️⃣ RECHERCHE PAR TÉLÉPHONE
    // ============================================
    if (clientData.phone) {
      // Recherche parallèle Valetier + Sirvoy
      const [valetierClient, sirvoyClient] = await Promise.all([
        findClientByPhone(clientData.phone),
        findSirvoyClientByPhone(clientData.phone)
      ]);
      
      if (valetierClient) {
        console.log('✅ Client VALETIER trouvé par téléphone:', valetierClient.id);
        return valetierClient;
      }
      
      if (sirvoyClient) {
        console.log('📦 Client SIRVOY trouvé par téléphone:', sirvoyClient.phone);
        return sirvoyClient;
      }
    }
    
    // ============================================
    // 3️⃣ RECHERCHE PAR NOM + PRÉNOM
    // ============================================
    if (clientData.name && clientData.firstName) {
      // Recherche parallèle Valetier + Sirvoy
      const [valetierClient, sirvoyClient] = await Promise.all([
        findClientByName(clientData.firstName, clientData.name),
        findSirvoyClientByName(clientData.firstName, clientData.name)
      ]);
      
      if (valetierClient) {
        console.log('✅ Client VALETIER trouvé par nom:', valetierClient.id);
        return valetierClient;
      }
      
      if (sirvoyClient) {
        console.log('📦 Client SIRVOY trouvé par nom:', `${sirvoyClient.firstName} ${sirvoyClient.name}`);
        return sirvoyClient;
      }
    }
    
    console.log('❌ Aucun client trouvé (Valetier + Sirvoy)');
    return null;
  } catch (error) {
    console.error('Erreur findExistingClient:', error);
    return null;
  }
}

/**
 * Cherche un client existant selon les critères de matching
 * (Email > Téléphone > Nom+Prénom)
 */
export async function findExistingClient_OLD(clientData) {
  try {
    // 1. Recherche par email (le plus fiable)
    if (clientData.email) {
      const client = await findClientByEmail(clientData.email);
      if (client) return client;
    }
    
    // 2. Recherche par téléphone
    if (clientData.phone) {
      const client = await findClientByPhone(clientData.phone);
      if (client) return client;
    }
    
    // 3. Recherche par nom ET prénom
    if (clientData.name && clientData.firstName) {
      const client = await findClientByName(clientData.firstName, clientData.name);
      if (client) return client;
    }
    
    return null;
  } catch (error) {
    console.error('Erreur findExistingClient:', error);
    return null;
  }
}

/**
 * Crée un nouveau client dans MongoDB
 */
export async function createClient(clientData) {
  try {
    const maxNumber = await getMaxClientNumber();
    const clientNumber = String(maxNumber + 1).padStart(6, '0');
    
    const newClient = {
      clientNumber,
      firstName: clientData.firstName || '',
      name: clientData.name || '',
      company: clientData.company || '',
      address: clientData.address || '',
      city: clientData.city || '',
      postalCode: clientData.postalCode || '',
      country: clientData.country || '',
      phone: clientData.phone || '',
      email: clientData.email || '',
      
      // Métadonnées
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      
// Statistiques VALETIER (initialisées avec Sirvoy si disponible)
totalReservations: clientData.sirvoyHistory?.totalReservations || 0,
totalCancellations: clientData.sirvoyHistory?.totalCancellations || 0,
cancellationCount: 0,
returnCount: 0,
lastReservationDate: clientData.sirvoyHistory?.lastVisit || null,
firstReservationDate: clientData.sirvoyHistory?.firstVisit || null,
isReturningClient: (clientData.sirvoyHistory?.totalReservations || 0) > 0,
      
      // Historique Sirvoy (sera ajouté par clientManager si existe)
      sirvoyHistory: clientData.sirvoyHistory || null,
      
      // Infos personnalisées
      tags: [],
      preferences: '',
      notes: '',
      
      // Membre du club
      isClubMember: false,
      clubMemberNumber: null,
      clubMemberSince: null,
      
      // Stats de réservations
      reservationsStats: {},
      
      // Clés de recherche normalisées
      searchKeys: {
        normalizedName: normalizeString(clientData.name),
        normalizedFirstName: normalizeString(clientData.firstName),
        normalizedEmail: normalizeString(clientData.email),
        normalizedPhone: normalizePhone(clientData.phone)
      }
    };
    
    const result = await mongoInsertOne('clients', newClient);
    
    const clientId = result.insertedId;
    
    // ✅ IMPORTANT : Ajouter le champ 'id' au document pour les recherches futures
    await mongoUpdateOne('clients', { _id: clientId }, { id: clientId });
    
    console.log('✅ Client créé dans MongoDB:', clientId);
    
    return {
      id: clientId,
      ...newClient
    };
  } catch (error) {
    console.error('Erreur création client:', error);
    throw error;
  }
}

/**
 * Récupère un client par son ID
 */
export async function getClientById(clientId) {
  try {
    // ✅ Chercher par le champ 'id' (Firebase ID), pas '_id' (MongoDB ObjectId)
    const clients = await mongoFind('clients', { id: clientId }, { limit: 1 });
    
    if (clients.length === 0) return null;
    
    return {
      id: clients[0].id,
      ...clients[0]
    };
  } catch (error) {
    console.error('Erreur récupération client:', error);
    return null;
  }
}

/**
 * Met à jour un client
 */
export async function updateClient(clientId, updateData) {
  try {
    // Toujours mettre à jour updatedAt
    const dataWithTimestamp = {
      ...updateData,
      updatedAt: new Date().toISOString()
    };
    
    // ✅ Utiliser le champ 'id' (Firebase ID), pas '_id'
    await mongoUpdateOne('clients', { id: clientId }, dataWithTimestamp);
    
    console.log('✅ Client mis à jour:', clientId);
    
    return true;
  } catch (error) {
    console.error('Erreur mise à jour client:', error);
    throw error;
  }
}

/**
 * Met à jour les infos d'un client depuis une réservation
 */
export async function updateClientFromReservation(clientId, reservationData) {
  try {
    const client = await getClientById(clientId);
    
    if (!client) {
      console.warn('Client non trouvé:', clientId);
      return;
    }
    
    const updateData = {
      // Mise à jour des coordonnées (dernière résa fait foi)
      firstName: reservationData.client.firstName || client.firstName,
      name: reservationData.client.name || client.name,
      company: reservationData.client.company || client.company,
      address: reservationData.client.address || client.address,
      city: reservationData.client.city || client.city,
      postalCode: reservationData.client.postalCode || client.postalCode,
      country: reservationData.client.country || client.country,
      phone: reservationData.client.phone || client.phone,
      email: reservationData.client.email || client.email,
      
      // Mise à jour des clés de recherche
      'searchKeys.normalizedName': normalizeString(reservationData.client.name),
      'searchKeys.normalizedFirstName': normalizeString(reservationData.client.firstName),
      'searchKeys.normalizedEmail': normalizeString(reservationData.client.email),
      'searchKeys.normalizedPhone': normalizePhone(reservationData.client.phone)
    };
    
    await updateClient(clientId, updateData);
  } catch (error) {
    console.error('Erreur updateClientFromReservation:', error);
  }
}

/**
 * Met à jour les statistiques d'un client
 */
export async function updateClientStats(clientId, reservationData) {
  try {
    const client = await getClientById(clientId);
    
    if (!client) {
      console.warn('Client non trouvé:', clientId);
      return;
    }
    
    const reservationDate = reservationData.checkIn;
    
    const updateData = {
      totalReservations: (client.totalReservations || 0) + 1,
      lastReservationDate: reservationDate,
      firstReservationDate: client.firstReservationDate || reservationDate,
      isReturningClient: (client.totalReservations || 0) + 1 > 1
    };
    
    await updateClient(clientId, updateData);
  } catch (error) {
    console.error('Erreur updateClientStats:', error);
  }
}

/**
 * Met à jour les stats de réservation d'un client
 */
export async function updateClientReservationStats(clientId, reservationNumber, status) {
  try {
    if (!clientId || !reservationNumber) {
      console.warn('⚠️ updateClientReservationStats: clientId ou reservationNumber manquant');
      return;
    }
    
    const client = await getClientById(clientId);
    
    if (!client) {
      console.warn(`⚠️ Client ${clientId} introuvable`);
      return;
    }
    
    const reservationsStats = client.reservationsStats || {};
    reservationsStats[reservationNumber] = { status };
    
    await updateClient(clientId, { reservationsStats });
    
    console.log(`✅ Stats client ${clientId} mises à jour : ${reservationNumber} → ${status}`);
  } catch (error) {
    console.error('❌ Erreur updateClientReservationStats:', error);
  }
}

/**
 * Recherche de clients par terme
 */
export async function searchClients(searchTerm, limit = 50) {
  try {
    if (!searchTerm || searchTerm.length < 2) return [];
    
    const normalizedTerm = searchTerm.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    
    const clients = await mongoFind('clients', {
      $or: [
        { 'searchKeys.normalizedFirstName': { $regex: normalizedTerm, $options: 'i' } },
        { 'searchKeys.normalizedName': { $regex: normalizedTerm, $options: 'i' } },
        { 'searchKeys.normalizedEmail': { $regex: normalizedTerm, $options: 'i' } }
      ]
    }, { limit });
    
    // ✅ Préférer client.id (Firebase ID) plutôt que _id (MongoDB ObjectId)
    return clients.map(client => ({
      ...client,
      id: client.id || client._id
    }));
  } catch (error) {
    console.error('Erreur recherche clients:', error);
    return [];
  }
}

/**
 * Récupère tous les clients (avec pagination optionnelle)
 */
export async function getAllClients(options = {}) {
  try {
    const clients = await mongoFind('clients', {}, {
      sort: { name: 1, firstName: 1 },
      skip: options.skip || 0,
      limit: options.limit || 1000
    });
    
    // ✅ Retourner les clients tels quels (ils ont déjà le bon champ 'id' depuis Firebase)
    return clients.map(client => ({
      ...client,
      id: client.id || client._id  // Préférer client.id (Firebase ID)
    }));
  } catch (error) {
    console.error('Erreur récupération clients:', error);
    return [];
  }
}

/**
 * Active le statut membre du club
 */
export async function activateClubMembership(clientId) {
  try {
    const client = await getClientById(clientId);
    
    if (!client) {
      console.warn('Client non trouvé:', clientId);
      return null;
    }
    
    // Si déjà membre, ne rien faire
    if (client.isClubMember) return client;
    
    const maxNumber = await getMaxClubMemberNumber();
    const clubMemberNumber = String(maxNumber + 1).padStart(4, '0');
    
    await updateClient(clientId, {
      isClubMember: true,
      clubMemberNumber,
      clubMemberSince: new Date().toISOString()
    });
    
    return {
      ...client,
      isClubMember: true,
      clubMemberNumber
    };
  } catch (error) {
    console.error('Erreur activation membre club:', error);
    throw error;
  }
}

/**
 * Désactive le statut membre du club
 */
export async function deactivateClubMembership(clientId) {
  try {
    const client = await getClientById(clientId);
    
    if (!client) {
      console.warn('Client non trouvé:', clientId);
      return null;
    }
    
    await updateClient(clientId, {
      isClubMember: false,
      formerClubMemberNumber: client.clubMemberNumber,
      clubMemberNumber: null,
      clubMemberSince: null,
      clubMemberRemovedAt: new Date().toISOString()
    });
    
    return {
      ...client,
      isClubMember: false
    };
  } catch (error) {
    console.error('Erreur désactivation membre club:', error);
    throw error;
  }
}

/**
 * Met à jour les tags d'un client
 */
export async function updateClientTags(clientId, tags) {
  try {
    await updateClient(clientId, { tags });
  } catch (error) {
    console.error('Erreur mise à jour tags:', error);
    throw error;
  }
}

/**
 * Met à jour les préférences et notes d'un client
 */
export async function updateClientPreferences(clientId, preferences, notes) {
  try {
    await updateClient(clientId, { preferences, notes });
  } catch (error) {
    console.error('Erreur mise à jour préférences:', error);
    throw error;
  }
}

/**
 * Supprime un client
 */
export async function deleteClient(clientId) {
  try {
    // ✅ Utiliser le champ 'id' (Firebase ID), pas '_id'
    await mongoDeleteOne('clients', { id: clientId });
    console.log('✅ Client supprimé:', clientId);
    return true;
  } catch (error) {
    console.error('Erreur suppression client:', error);
    throw error;
  }
}

export default {
  normalizeString,
  findExistingClient,
  createClient,
  getClientById,
  updateClient,
  updateClientFromReservation,
  updateClientStats,
  updateClientReservationStats,
  searchClients,
  getAllClients,
  activateClubMembership,
  deactivateClubMembership,
  updateClientTags,
  updateClientPreferences,
  deleteClient,
  getMaxClientNumber,
  getMaxClubMemberNumber,
  mongoInsertOne  // ✅ Ajouté pour la migration
};
